(() => {
  'use strict';
  if (customElements.get('tz-birthday-club')) return;
  const inferred = { son: 'boy', daughter: 'girl', niece: 'girl', nephew: 'boy' };
  const relationships = new Set([...Object.keys(inferred), 'grandchild', 'gift']);
  const uuid = () => crypto.randomUUID();
  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const dateParts = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [y, m, d] = value.split('-').map(Number);
    const test = new Date(0);
    test.setFullYear(y, m - 1, d);
    test.setHours(0, 0, 0, 0);
    return y > 0 && test.getFullYear() === y && test.getMonth() === m - 1 && test.getDate() === d && value <= today() ? [y, m, d] : null;
  };
  const phoneNumber = value => {
    let p = value.trim().replace(/[\s().-]/g, '');
    if (/^03\d{9}$/.test(p)) p = `+92${p.slice(1)}`;
    else if (/^923\d{9}$/.test(p)) p = `+${p}`;
    else if (p.startsWith('00')) p = `+${p.slice(2)}`;
    return /^\+[1-9]\d{6,14}$/.test(p) ? p : null;
  };
  class BirthdayClub extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector('[data-form]')) return;
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.messages = Object.fromEntries([...this.querySelectorAll('[data-message]')].map(el => [el.dataset.message, el.textContent]));
      this.form = this.querySelector('[data-form]');
      this.panels = [...this.querySelectorAll('[data-step]')];
      this.childProfiles ||= [];
      this.step ??= 0;
      this.editing ??= null;
      this.draftActive ??= true;
      this.desktopMedia = matchMedia('(min-width: 1024px)');
      this.on('change', () => this.show(this.step, false), options, this.desktopMedia);
      this.on('submit', event => { if (this.nativeContactReady) return; event.preventDefault(); this.advance(); }, options, this.form);
      this.on('click', event => {
        const button = event.target.closest('button');
        if (!button || this.busy || (this.pending && !button.hasAttribute('data-next'))) return;
        if (button.hasAttribute('data-add-draft')) { if (this.draftActive && (this.value('first_name') || this.selected('relationship'))) { if (!this.validate(0)) { this.show(0); return; } if (!this.validate(1)) { this.show(1); return; } this.commitChild(); } this.add(); }
        if (button.hasAttribute('data-next')) this.advance();
        if (button.hasAttribute('data-back')) this.back();
        if (button.hasAttribute('data-add')) { if (this.expanded && this.step !== 3 && this.draftActive) { if (!this.validate(0)) { this.show(0); return; } if (!this.validate(1)) { this.show(1); return; } this.commitChild(); } this.add(); }
        if (button.hasAttribute('data-cancel')) this.cancelChild();
        if (button.dataset.edit !== undefined) this.edit(Number(button.dataset.edit));
        if (button.dataset.remove !== undefined) this.remove(Number(button.dataset.remove));
      }, options);
      this.on('input', event => {
        if (!this.started) { this.started = true; this.track('started'); }
        if (['first_name','date_of_birth','relationship','gender','interests'].includes(event.target.name)) this.draftActive = true;
        if (event.target.name === 'date_of_birth') this.updateAge();
        if (event.target.name) this.clearError(event.target.name);
      }, options);
      this.on('change', event => {
        if (event.target.name === 'relationship') this.updateGender(true);
        if (event.target.name === 'interests') this.clearError('interests');
      }, options);
      this.on('keydown', () => { this.keyboard = true; }, options);
      this.on('pointerdown', () => { this.keyboard = false; }, options);
      this.input('date_of_birth').max = today();
      const seen = new Set();
      this.querySelectorAll('[name=interests]').forEach(input => {
        const valid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(input.value) && !seen.has(input.value);
        input.closest('label').hidden = !valid;
        input.dataset.valid = String(valid);
        if (valid) seen.add(input.value);
      });
      this.querySelector('[data-empty-interests]').hidden = seen.size > 0;
      const canPreview = this.dataset.enabled === 'true' || this.dataset.preview === 'true';
      this.querySelector('[data-wizard]').hidden = !canPreview;
      const another = this.querySelector('[data-another]'); if (another) another.hidden = !canPreview;
      if (this.querySelector('[data-contact-success]')) this.step = 3;
      this.show(this.step, false);
      this.track('view');
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.network?.abort();
      this.animation?.cancel();
    }
    on(name, fn, options, target = this) { target.addEventListener(name, fn, options); }
    input(name) { return this.form.querySelector(`[name="${name}"]`); }
    value(name) { return this.input(name).value.trim(); }
    message(key, values = {}) {
      let text = this.messages[key] || key;
      Object.entries(values).forEach(([k, v]) => { text = text.replace(`{${k}}`, String(v)); });
      return text;
    }
    selected(name) { return this.form.querySelector(`[name="${name}"]:checked`)?.value || ''; }
    updateGender(reset = false) {
      const conditional = ['grandchild', 'gift'].includes(this.selected('relationship'));
      const field = this.querySelector('[data-gender]');
      field.hidden = !conditional;
      field.disabled = !conditional || (!this.expanded && this.step !== 0) || this.busy || !!this.pending;
      if (reset) this.form.querySelector('[name=gender][value=unknown]').checked = true;
    }
    updateAge() {
      const parts = dateParts(this.value('date_of_birth'));
      let value = this.message('age_placeholder');
      if (parts) {
        const now = new Date();
        let age = now.getFullYear() - parts[0];
        if (now.getMonth() + 1 < parts[1] || (now.getMonth() + 1 === parts[1] && now.getDate() < parts[2])) age--;
        value = (age === 0 ? this.message('age_baby') : age === 1 ? this.message('age_year') : this.message('age_years', { count: age }));
      }
      this.querySelector('[data-age]').textContent = value;
    }
    clearError(name) {
      const el = this.querySelector(`[data-error="${name}"]`);
      if (el) { el.hidden = true; el.textContent = ''; }
      this.form.querySelectorAll(`[name="${name}"]`).forEach(input => input.removeAttribute('aria-invalid'));
    }
    error(name, message) {
      const el = this.querySelector(`[data-error="${name}"]`);
      if (el) { el.textContent = message; el.hidden = false; }
      this.form.querySelectorAll(`[name="${name}"]`).forEach(input => input.setAttribute('aria-invalid', 'true'));
    }
    validate(step) {
      const errors = [];
      if (step === 0) {
        if (!relationships.has(this.selected('relationship'))) errors.push(['relationship', 'error_relationship']);
        if (!this.value('first_name') || this.value('first_name').length > 60 || /[\u0000-\u001f\u007f]/.test(this.value('first_name'))) errors.push(['first_name', 'error_name']);
        if (!dateParts(this.value('date_of_birth'))) errors.push(['date_of_birth', 'error_birthday']);
      }
      if (step === 1 && !this.interests().length) errors.push(['interests', 'error_interests']);
      if (step === 2) {
        if (!this.value('name') || this.value('name').length > 100) errors.push(['name', 'error_adult_name']);
        if (!this.value('email') || !this.input('email').validity.valid) errors.push(['email', 'error_email']);
        if (!phoneNumber(this.value('phone'))) errors.push(['phone', 'error_phone']);
        if (!this.input('adult_confirm').checked) errors.push(['adult_confirm', 'error_adult_confirm']);
      }
      errors.forEach(([field, key]) => this.error(field, this.message(key)));
      if (errors.length) this.input(errors[0][0])?.focus();
      return errors.length === 0;
    }
    interests() { return [...this.querySelectorAll('[name=interests]:checked[data-valid=true]')].map(el => el.value); }
    show(step, focus = true) {
      this.step = step;
      this.dataset.currentStep = String(step);
      this.expanded = this.dataset.expanded === 'true' && this.desktopMedia.matches;
      this.dataset.layout = this.expanded ? 'expanded' : 'wizard';
      this.panels.forEach((panel, index) => { panel.hidden = step === 3 || (!this.expanded && index !== step); panel.disabled = panel.hidden || this.busy || !!this.pending; });
      this.form.hidden = step === 3;
      this.querySelector('[data-success]').hidden = step !== 3;
      this.querySelector('[data-unsaved]').hidden = step === 3;
      this.querySelectorAll('.bc-progress li').forEach((el, i) => {
        el.removeAttribute('aria-current');
        if (i === step) el.setAttribute('aria-current', 'step');
        el.toggleAttribute('data-complete', i < step);
      });
      const label = this.message(['child_step', 'interests_step', 'adult_step', 'done_step'][step]);
      this.querySelector('[data-step-status]').textContent = this.message('step_status', { step: step + 1, label });
      const back = this.querySelector('[data-back]');
      back.hidden = step === 0 || this.expanded;
      back.disabled = this.busy || !!this.pending;
      this.querySelector('[data-cancel]').hidden = !this.childProfiles.length || step > 1;
      this.querySelector('[data-cancel]').disabled = this.busy || !!this.pending;
      const next = this.querySelector('[data-next]');
      next.textContent = this.busy ? this.message('saving') : this.pending ? this.message('retry') : (step === 2 || this.expanded) ? next.dataset.join : step === 0 ? (next.dataset.interests || next.dataset.continue) : (next.dataset.details || next.dataset.continue);
      next.disabled = !!this.busy;
      this.form.setAttribute('aria-busy', String(!!this.busy));
      this.updateGender();
      if (focus) {
        const panel = step === 3 ? this.querySelector('[data-success]') : this.panels[step];
        panel.querySelector('h2').focus({ preventScroll: true });
        const rect = panel.getBoundingClientRect();
        if (rect.top < 0 || rect.top > innerHeight * .6) panel.scrollIntoView({ block: 'start', behavior: 'instant' });
        if (this.dataset.motion !== 'none' && !this.keyboard && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.animation?.cancel();
          const from = this.dataset.motion === 'slide' ? { opacity: .5, transform: 'translateY(6px)' } : { opacity: .5 };
          this.animation = panel.animate([from, { opacity: 1, transform: 'none' }], { duration: 160, easing: 'cubic-bezier(.23,1,.32,1)' });
        }
      }
    }
    advance() {
      if (this.busy) return;
      if (this.pending) { this.submit(); return; }
      if (this.expanded) {
        if (this.draftActive) { for (const step of [0,1]) { if (!this.validate(step)) { this.show(step, false); this.panels[step].querySelector('[aria-invalid=true]')?.focus(); return; } } this.commitChild(); }
        if (!this.validate(2) || !this.childProfiles.length) return;
        this.submit(); return;
      }
      if (!this.validate(this.step)) return;
      if (this.step === 0) { this.track('child_completed'); this.show(1); return; }
      if (this.step === 1) {
        this.commitChild(); this.show(2); return;
      }
      if (this.step === 2 && this.childProfiles.length) this.submit();
    }
    commitChild() {
        const child = {
          client_id: this.editing === null ? uuid() : this.childProfiles[this.editing].client_id,
          first_name: this.value('first_name'), relationship: this.selected('relationship'), date_of_birth: this.value('date_of_birth'),
          gender: inferred[this.selected('relationship')] || this.selected('gender') || 'unknown', interests: this.interests()
        };
        if (this.editing === null) this.childProfiles.push(child); else this.childProfiles[this.editing] = child;
        if (this.editing === null) this.editing = this.childProfiles.length - 1;
        this.renderChildren(); this.track('interests_completed'); 
      this.draftActive = false;
    }
    back() {
      if (this.step === 2) { this.edit(this.childProfiles.length - 1); this.show(1); }
      else this.show(Math.max(0, this.step - 1));
    }
    add() {
      if (this.step === 3) { this.childProfiles = []; this.savedLead = null; this.pending = null; this.uncertain = false; }
      this.editing = null;
      this.draftActive = true;
      this.input('first_name').value = '';
      this.input('date_of_birth').value = '';
      this.querySelectorAll('[name=relationship],[name=interests]').forEach(el => { el.checked = false; });
      this.querySelector('[name=gender][value=unknown]').checked = true;
      this.querySelectorAll('[data-error]').forEach(el => this.clearError(el.dataset.error));
      this.querySelector('[data-submit-error]').hidden = true;
      this.updateAge(); this.track('add_child'); this.show(0);
    }
    cancelChild() { this.edit(this.childProfiles.length - 1); this.draftActive = false; this.renderChildren(); this.show(2); }
    edit(index) {
      const child = this.childProfiles[index];
      if (!child) return;
      this.editing = index;
      this.draftActive = true;
      this.input('first_name').value = child.first_name;
      this.input('date_of_birth').value = child.date_of_birth;
      this.querySelectorAll('[name=relationship]').forEach(el => { el.checked = el.value === child.relationship; });
      this.querySelectorAll('[name=gender]').forEach(el => { el.checked = el.value === child.gender; });
      this.querySelectorAll('[name=interests]').forEach(el => { el.checked = child.interests.includes(el.value); });
      this.updateAge(); this.show(0);
    }
    remove(index) {
      this.childProfiles.splice(index, 1);
      if (!this.childProfiles.length) this.add(); else { this.edit(this.childProfiles.length - 1); this.draftActive = false; this.renderChildren(); this.show(2); }
    }
    childRow(child, index, editable) {
      const row = document.createElement('li'); row.className = 'bc-child-row';
      const name = document.createElement('strong'); name.textContent = child.first_name; row.append(name);
      const dob = document.createElement('p');
      const [year, month, day] = child.date_of_birth.split('-').map(Number);
      const d = new Date(0); d.setFullYear(year, month - 1, day);
      dob.textContent = this.message('birthday') + ': ' + new Intl.DateTimeFormat(document.documentElement.lang || 'en', { day: 'numeric', month: 'long' }).format(d); row.append(dob);
      const interests = document.createElement('p');
      const labels = child.interests.map(value => [...this.querySelectorAll('[name=interests]')].find(el => el.value === value)?.closest('label').querySelector('[data-interest-label]').textContent || value);
      interests.textContent = this.message('interests_step') + ': ' + labels.join(' · '); row.append(interests);
      if (editable) ['edit', 'remove'].forEach(action => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'bc-link'; button.dataset[action] = String(index); button.textContent = this.message(action); button.setAttribute('aria-label', `${this.message(action)} ${child.first_name}`); row.append(button);
      });
      return row;
    }
    renderChildren() { this.querySelector('[data-children]').replaceChildren(...this.childProfiles.map((c, i) => this.childRow(c, i, true))); }
    track(name) {
      if (this.dataset.analytics !== 'true' || window.Shopify?.designMode || this.dataset.preview === 'true') return;
      try {
        if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
        const detail = { event: `birthday_club_${name}`, step: this.step + 1 };
        this.dispatchEvent(new CustomEvent('tz:birthday-club:analytics', { bubbles: true, detail }));
        if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      } catch { /* Analytics must never interrupt registration. */ }
    }
    endpoint() {
      const path = this.dataset.endpoint || '';
      if (!/^\/apps\/[a-z0-9][a-z0-9/-]*$/i.test(path)) throw new Error('configuration');
      return path.replace(/\/$/, '');
    }
    async fetchJSON(url, options = {}) {
      const controller = new AbortController(); this.network = controller;
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal, credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers: { Accept: 'application/json', ...options.headers } });
        let data;
        try { data = await response.json(); } catch { throw new Error('uncertain'); }
        return { response, data };
      } finally { clearTimeout(timer); }
    }
    async saveLead() {
      const base = 'https://app.toyzone.pk/birthday-club/api';
      if (!this.pending) {
        this.pending = {
          version: 1, request_id: uuid(), country_code: this.dataset.country || null,
          adult: { name: this.value('name'), email: this.value('email'), phone: this.value('phone') },
          children: this.childProfiles.map(child => ({ ...child, interests: [...child.interests] })),
          consent: { adult_confirmed: this.input('adult_confirm').checked, marketing_requested: this.input('marketing_consent').checked, wording: this.message('consent'), copy_version: 'birthday-club-leads-v1', channels: [] }
        };
      }
      const error = this.querySelector('[data-submit-error]');
      const send = async (url, options = {}) => {
        const controller = new AbortController(); this.network = controller;
        const timer = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(url, { ...options, signal: controller.signal, credentials: 'omit', mode: 'cors', cache: 'no-store', redirect: 'error', headers: { Accept: 'application/json', ...options.headers } });
          return { response, data: await response.json() };
        } finally { clearTimeout(timer); }
      };
      this.busy = true; this.show(2, false);
      let posted = false;
      try {
        const session = await send(base + '/session');
        if (!session.response.ok || typeof session.data.token !== 'string') throw new Error('session');
        posted = true;
        const { response, data } = await send(base + '/intake', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Birthday-Token': session.data.token }, body: JSON.stringify(this.pending) });
        if (response.ok && data.status === 'saved' && data.request_id === this.pending.request_id && typeof data.submission_id === 'string' && data.submission_id && data.child_count === this.pending.children.length) {
          this.savedLead = data.submission_id;
          return true;
        }
        if ([403,413,415,422,429].includes(response.status) && !this.uncertain) {
          this.pending = null;
          error.textContent = this.message(response.status === 422 ? 'intake_validation' : 'intake_error');
          return false;
        }
        throw new Error('unconfirmed');
      } catch {
        if (posted) this.uncertain = true;
        if (!this.uncertain) this.pending = null;
        error.textContent = this.message('intake_error');
        return false;
      } finally {
        this.busy = false; this.show(2, false);
        if (!this.savedLead) { error.hidden = false; error.focus(); }
      }
    }
    async submit() {
      const error = this.querySelector('[data-submit-error]');
      error.hidden = true;
      if (this.dataset.enabled !== 'true' || window.Shopify?.designMode || this.dataset.preview === 'true') {
        error.textContent = this.message('error_unavailable'); error.hidden = false; error.focus(); return;
      }
      if (this.nativeContactReady || this.busy) return;
      if (this.dataset.intakeEnabled === 'true' && !this.savedLead && !(await this.saveLead())) return;
      const lines = ['ToyZone Birthday Club request', this.savedLead ? 'Lead saved securely. No account or marketing subscription created.' : 'Manual contact request; no membership or subscription created.', ''];
      if (this.savedLead) lines.push('Database reference: ' + this.savedLead, '');
      this.childProfiles.forEach((child, index) => {
        lines.push(`Child ${index + 1}`, `First name: ${child.first_name}`, `Birthday: ${child.date_of_birth}`, `Relationship: ${child.relationship}`, `Gender: ${child.gender}`, `Interests: ${child.interests.join(', ')}`, '');
      });
      const optedIn = this.input('marketing_consent').checked;
      lines.push('Adult confirmed: Yes', `Marketing preference: ${optedIn ? 'Requested' : 'Not requested'}`, `Preference wording: ${this.message('consent')}`, 'Preference recorded only; no email, SMS or WhatsApp subscription has been activated.');
      const fields = {
        'contact[name]': this.value('name'),
        'contact[email]': this.value('email'),
        'contact[phone]': phoneNumber(this.value('phone')),
        'contact[body]': lines.join('\n')
      };
      const container = this.querySelector('[data-contact-fields]');
      container.replaceChildren();
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value; container.append(input);
      });
      // Native navigation preserves Shopify's own contact response and challenge flow.
      // Do not show success until Liquid reports form.posted_successfully?.
      this.nativeContactReady = true;
      try { this.form.requestSubmit(); }
      finally { this.nativeContactReady = false; }
    }
  }
  customElements.define('tz-birthday-club', BirthdayClub);
})();
